<template>
  <div id="standalone-places-searchbox-container" class="container">
    <div ref="domNode" class="places-searchbox" :class="{ 'is-smt': isSmt }">
      <label for="search-field" class="label">
        <span class="accessibility">{{
          useMicroCopy("servicelocator.location.label")
        }}</span>
        <SvgIcon :name="!isSmt ? 'func_search' : 'func_locator'" class="icon-search" />
      </label>
      <input
        id="search-field"
        ref="buttonNode"
        v-model="query"
        tabindex="0"
        type="text"
        :placeholder="useMicroCopy('servicelocator.placeholder')"
        name="search-field"
        class="search-field"
        aria-haspopup="true"
        aria-controls="menu2"
      />
      <SimpleButton
        class="search-button"
        solid
        @click="query?.length ? handleSearch() : ''"
        v-if="!isSmt"
      >
        <span class="text">{{
          useMicroCopy("servicelocator.searchcta.label")
        }}</span>
        <SvgIcon name="func_search" class="icon-search" />
      </SimpleButton>

      <ul
        v-show="showSuggestions"
        id="menu2"
        ref="menuNode"
        class="suggestions"
        role="menu"
        aria-labelledby="menubutton"
      >
        <li v-for="item in suggestions" :key="item.place_id" class="suggestion">
          <button role="menuitem">
            <span class="main">{{ item.structured_formatting.main_text }}</span>
            <span class="secondary text-70">{{
              item.structured_formatting.secondary_text
            }}</span>
          </button>
        </li>
        <li class="geoloc">
          <button role="menuitem" @click="searchWithCurrentLocation">
            <SvgIcon name="func_focus" />
            {{ useMicroCopy("servicelocator.usecurrentlocation") }}
          </button>
        </li>
      </ul>
    </div>
    <div class="filters" v-if="!isSmt">
      <div
        v-for="filter in allowedFacilityTypeGroupKeysList"
        :key="filter"
        class="filter"
      >
        <FormCheckbox
          v-model="filter.isIncluded"
          :name="filter.name"
          :label="
            useMicroCopy(
              `servicelocator.searchengine.label.${filter.name?.replace(
                '-',
                ''
              )}`
            )
          "
          @change="
            trackingStore.updateKeywords(query);
            updateFilters($event, filter.name);
          "
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { usePlacesAutocomplete } from "vue-use-places-autocomplete";
import { useGeolocation } from "@vueuse/core";
import { useSnakeCaseFilter } from "@/composables/SnakecaseFilter";
import { navigateTo } from "nuxt/app";
import { useTrackingStore } from "@/stores/tracking";
import { nextTick } from 'vue';

const config = useRuntimeConfig();
const trackingStore = useTrackingStore();

const isSmt = computed(() => props.location === "smt");

const props = defineProps({
  location: {
    type: String,
    default: "",
  },
  includeFacilityTypeGroupKey: {
    type: [String, Array],
    required: false,
    default: "",
  },
});

const emit = defineEmits(['update:query', 'update:suggestions']);
const allowedFacilityTypeGroupKeysList = await GetFacilityTypeGroupKeys(
  props.includeFacilityTypeGroupKey,
  null
);

const updateFilters = (event, name) => {
  const filter = allowedFacilityTypeGroupKeysList?.find(
    (item) => item.name === name
  );
  filter.isIncluded = event.target.checked;
};

// Google Places start
const query = ref("");
const [language, region] = config.public["timeZone"].split("-");

const { suggestions } = usePlacesAutocomplete(query, {
  apiKey: config.public["gmapsApiKey"],
  apiOptions: {
    language: language,
    region: region,
  },
  debounce: 250,
  minLengthAutocomplete: 3,
  autocompletionRequest: {
    componentRestrictions: {
      country: region.toLowerCase(),
    },
  },
});

const currentLocation = ref(null);
const { coords, pause } = await useGeolocation();

// utilitaires
const waitForGoogleMaps = () =>
  new Promise((resolve) => {
    if (process.server) return resolve(null); // ne rien faire côté SSR
    const check = () => {
      if (window.google && window.google.maps && window.google.maps.Geocoder) {
        resolve(true);
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });

const geocodeByPlaceId = async (placeId) => {
  await waitForGoogleMaps();
  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId }, (results, status) => {
      if (status === "OK") resolve(results);
      else reject(new Error("Geocode failed: " + status));
    });
  });
};

const geocodeByLatLng = async ({ lat, lng }) => {
  await waitForGoogleMaps();
  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK") resolve(results);
      else reject(new Error("Reverse geocode failed: " + status));
    });
  });
};

watch(coords, async (newCoords, oldCoords) => {
  if (
    !chosenPlaceId.value &&
    newCoords.latitude &&
    newCoords.latitude !== Infinity &&
    (!oldCoords.latitude || oldCoords.latitude === Infinity)
  ) {
    const curr = await geocodeByLatLng({
      lat: newCoords.latitude,
      lng: newCoords.longitude,
    });
    currentLocation.value = curr[0].formatted_address;
  }
});

const searchWithCurrentLocation = () => {
  chosenPlaceId.value = ""; // ✅ allow geolocation updates again
  query.value = currentLocation?.value;
};
// Google places end

// Search Facilities start
const chosenPlaceId = ref("");

const getDetails = async (placeId = chosenPlaceId.value) => {
  chosenPlaceId.value = placeId;
  const details = await geocodeByPlaceId(placeId);
  query.value = details[0].formatted_address;
  return details;
};

// Search Facilities end

// setup
const domNode = ref();
const buttonNode = ref();
const menuNode = ref();
const menuitemNodes = ref([]);

// methods
const updateMenuitemNodes = () => {
  menuitemNodes.value = [];
  const nodes = domNode.value?.querySelectorAll('[role="menuitem"]');

  for (let i = 0; i < nodes?.length; i++) {
    const menuitem = nodes[i];
    menuitemNodes.value.push(menuitem);
    if (menuitem.textContent.trim().length > 0) {
      trackingStore.updateKeywords(suggestions?.value?.[0]?.description);
    }
  }
};

const onSelectMenuitem = async (_, index) => {
  if (index >= suggestions?.value?.length) {
    searchWithCurrentLocation();
  } else {
    const item = suggestions.value[index];

    const data = await getDetails(item?.place_id);

    trackingStore.sendTrackingData({
      event: "click.navigation",
      click: "search",
      click_chapter1: "hero",
      click_chapter2: "search_bar",
      ise_keyword: useSnakeCaseFilter(item?.description),
    });
    return !!data;
  }
};

const { showDropdown: showSuggestions } = useDropdownNavigation(
  buttonNode,
  menuitemNodes,
  domNode,
  onSelectMenuitem,
  false
);

watch(
  suggestions,
  async (newVal) => {
    emit('update:suggestions', newVal);

    if (!newVal?.length) return;

    await nextTick();
    updateMenuitemNodes();
  },
  { flush: "post" }
);

watch(
  query,
  (newVal) => {
    emit('update:query', newVal);
  }
);

const handleSearch = (forceFilters = null) => {
  setTimeout(async () => {
    if (suggestions.value?.length) {
      trackingStore.updateKeywords(suggestions.value?.[0]?.description || "");
      trackingStore.sendTrackingData({
        event: "click.navigation",
        click: "search",
        click_chapter1: useSnakeCaseFilter(props.location || ""),
        click_chapter2: "search_bar",
        ise_keyword: useSnakeCaseFilter(trackingStore.getKeywords),
      });
      chosenPlaceId.value = chosenPlaceId.value?.length
        ? chosenPlaceId.value
        : suggestions.value[0].place_id;

      const locator = await useCustomAsyncStoryblok("", {
        version: config.public["storyblokVersion"],
        content_type: "page-locator",
        starts_with: config.public["storyblokCountryPrefix"],
      })
        .then((response) => {
          if (response.value.stories.length > 0) {
            return strippedURL(response.value.stories[0].full_slug);
          }
          return "/";
        })
        .catch((error) => {
          console.log(error); // eslint-disable-line
        });

      const includedFilters = allowedFacilityTypeGroupKeysList?.length
        ? allowedFacilityTypeGroupKeysList
            ?.filter((item) => item.isIncluded)
            .map((item) => item.name)
            .join("|")
        : "";
      const serviceLocatorURL = !forceFilters ? `${locator}?placeid=${chosenPlaceId.value}&includedFilters=${includedFilters}` : `${locator}?placeid=${chosenPlaceId.value}&includedFilters=${forceFilters}`;
      window.location.href = serviceLocatorURL;
    } else {
      console.error("No suggestions available to perform the search action");
    }
  });
};

defineExpose({ handleSearch, suggestions, query })
</script>

<style lang="scss" scoped>
.places-searchbox {
  z-index: 1;
  padding: 0.25rem 0.25rem 0.25rem 1.12rem;
  @include pair-7;
  border-radius: $radius-m;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  height: 3.75rem;

  @include for-tablet-landscape-up {
    padding-left: 1.5rem;
    border-radius: $radius-l;
    height: 5rem;
  }

  &.is-smt {
    background-color: var(--solid-05);

    @include for-tablet-landscape-up {
      height: 64px;
    }

    .search-field {
      background-color: transparent;
      outline: none;
      font-size: 1rem;
    }
    svg {
      opacity: 0.3;
    }
    ul {
      width: 100%;
    }

    @include for-tablet-landscape-down {
      .suggestions {
        bottom: calc(100% + 1rem);
        top: auto;
        overflow-y: auto;
        max-height: calc(100vh - 180px);
      }
    }
  }

  .label {
    display: block;
    @include size-2xs;
    .icon-search {
      width: 1.5rem;
      height: auto;
      margin-top: 0.2rem;
      @include for-phone-only {
        display: none;
      }
    }
  }

  .search-field {
    appearance: none;
    border: none;
    margin: 0;
    display: block;
    padding: 0;
    height: 100%;
    width: 100%;
    @include size-m;

    @include for-tablet-landscape-up {
      @include size-s;
    }
  }
}

.suggestions {
  border-radius: $radius-s;
  position: absolute;
  z-index: 3;
  top: calc(100% + 1rem);
  left: 0;
  width: 90vw;
  box-shadow: 0 0.25rem 1rem 0 var(--text-50);
  overflow: hidden;

  @include for-tablet-landscape-up {
    width: 40vw;
  }

  button {
    display: block;
    width: 100%;
    text-align: left;
    appearance: none;
    border: none;
    border-radius: 0;
    background: var(--solid-07);
    padding: 1.2rem;
    cursor: pointer;

    @include for-tablet-landscape-up {
      padding: 1rem 2.25rem;
    }

    &:focus,
    &:hover {
      @include pair-2;
      font-weight: bold;
    }
  }
}

.suggestion {
  span {
    display: block;

    &.main {
      @include size-s;
    }

    &.secondary {
      @include size-2xs;
    }
  }
}
.geoloc {
  button {
    @include size-s;
    font-weight: bold;
    display: flex;
    gap: 0.5rem;
  }
}

:deep(.simple-button) {
  color: var(--solid-07) !important;
  border-radius: $radius-m !important;
  height: 100%;
  @include for-tablet-landscape-up {
    padding-right: 2rem !important;
    padding-left: 2rem !important;
  }
  .icon-search {
    width: 1.25rem !important;
    height: auto !important;

    path {
      fill: none !important;
    }
  }
}

.suggestion + .geoloc {
  border-top: $border-grey;
}

.filters {
  display: inline-flex;
  gap: 0.75rem 1.25rem;
  flex-wrap: wrap;
  padding-top: 1.5rem;
  &.hidden {
    display: none;
  }
}

.filter {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;

  @include for-tablet-portrait-up {
    width: auto;
  }
}

.search-button {
  .text {
    display: none;
  }
  @include for-tablet-portrait-up {
    .text {
      display: inline-block;
    }
    .icon-search {
      display: none;
    }
  }
}

.checkbox :deep(input[type="checkbox"]) {
  @include for-desktop-down {
    width: 1.25rem !important;
    height: 1.25rem !important;
  }
}
</style>
